import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Private visit tracking for the main site and the Ghar360 page (see public/track-visit.php,
// which has the same page list). Sends one beacon per page per browser session, only on
// the live site. Signing in to the stats page (/site-stats) turns tracking off for that
// browser, so your own visits are not counted.

export const NO_TRACK_KEY = "rionick_no_track";
const SOURCE_KEY = "rionick_visit_source";

// Tracked pages and the names shown on the stats page
export const TRACKED_PAGES = {
  "/": "Home",
  "/about-us": "About",
  "/best-3d-services-india": "3D Modeling & Animation",
  "/viral-cgi-videos": "CGI Videos",
  "/graphic-designing-services-india": "Graphic & Motion Design",
  "/game-development-services-india": "Game Development",
  "/best-digital-marketing-services-india": "Digital Marketing",
  "/best-interior-designing-india": "Interior Designing",
  "/hall-of-fame": "Hall of Fame",
  "/partners": "Partners",
  "/contact-us": "Contact",
  "/Careers": "Careers",
  "/privacy-policy": "Privacy Policy",
  "/ghar360": "Ghar360",
};

const LIVE_HOSTS = ["rionick.com", "www.rionick.com"];

// Where the visit came from: a ?ref= / ?utm_source= tag on the link wins, then the
// referring site, otherwise "direct" (typed, bookmarked, or opened from an app that
// hides the referrer, which WhatsApp and many email apps do)
const detectSource = () => {
  const params = new URLSearchParams(window.location.search);
  const tagged = params.get("ref") || params.get("utm_source") || params.get("source");
  if (tagged) return tagged.toLowerCase().slice(0, 60);

  const referrer = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : "";
  if (!referrer || referrer.endsWith("rionick.com")) return "direct";
  if (/whatsapp|wa\.me/.test(referrer)) return "whatsapp";
  if (/linkedin|lnkd\.in/.test(referrer)) return "linkedin";
  if (/instagram/.test(referrer)) return "instagram";
  if (/facebook|fb\.com|fb\.me/.test(referrer)) return "facebook";
  if (/google\./.test(referrer)) return "google";
  if (/bing\.com/.test(referrer)) return "bing";
  if (/mail\.|outlook|gmail/.test(referrer)) return "email";
  if (/t\.co$|twitter|x\.com/.test(referrer)) return "x";
  if (/chatgpt|openai|perplexity|claude\.ai|gemini/.test(referrer)) return "ai assistant";
  return referrer;
};

// The source of the first page in a session applies to every page viewed after it
const getSource = () => {
  const saved = sessionStorage.getItem(SOURCE_KEY);
  if (saved) return saved;
  const source = detectSource();
  sessionStorage.setItem(SOURCE_KEY, source);
  return source;
};

export const trackVisit = (path) => {
  try {
    if (!TRACKED_PAGES[path]) return;
    if (!LIVE_HOSTS.includes(window.location.hostname)) return;
    if (localStorage.getItem(NO_TRACK_KEY) === "1") return;
    const sessionKey = `rionick_visited_${path}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");

    const body = JSON.stringify({
      path,
      source: getSource(),
      referrer: document.referrer ? new URL(document.referrer).hostname : "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      language: navigator.language || "",
    });

    fetch("/track-visit.php", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch (error) {
    // Tracking must never break the page
  }
};

// Mounted once inside the router: tracks every page the visitor opens
export const VisitTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    trackVisit(pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname);
  }, [pathname]);
  return null;
};
